/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **A blocking confirm — one question, two answers, nothing armed and invisible.**
 *
 * Built 2026-08-21 (#1129) to replace a two-press Escape confirm that was worse than what it
 * replaced. That one set a flag on the first press and closed on the second, with the question
 * rendered in the header — and the flag survived everything that was not a keystroke, so:
 *
 * > *"I make a change, hit esc. Then go to a new page, hit esc again, lose data."* — Todd
 *
 * The operator's second Escape was, to them, a **first** Escape on a different screen. State that
 * arms an irreversible action and is not visibly attached to the thing that armed it is a trap, and
 * the fix is not better clearing rules — it is not having the state. A dialog is either on screen or
 * it is not, and every route out of it is deliberate.
 *
 * ## What this is for
 *
 * The console has three confirms today, all hand-rolled and none alike: the wizard's, the detail
 * page's two-press Delete, and the clone overlay. #904 already anticipates them converging —
 * *"disable, delete and transfer then converge on one confirm pattern console-wide"*. This is that
 * component; adopt it rather than growing a fourth.
 *
 * ## Escape means cancel, always
 *
 * The safe direction, and the one every platform dialog uses. It also has to **stop propagating** —
 * this renders inside surfaces that have their own Escape handling, and a cancel that also closed
 * the wizard behind it would be the bug this was built to fix, wearing a different hat.
 */

import React from 'react';
import { Box, Button, HStack, Portal, Text, VStack } from '@chakra-ui/react';
import { useBackdropDismiss } from './useBackdropDismiss';

export interface ConfirmDialogProps {
  /** The question, as a question. Short enough to read in one pass. */
  title: string;
  /** What confirming will do, in the operator's terms — the outcome, never the mechanism (#1045). */
  body?: React.ReactNode;
  /** Label for the destructive answer. Say the act — "Discard", "Delete" — never "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for anything that loses work or data; `neutral` for a reversible choice. */
  tone?: 'danger' | 'neutral';
  /**
   * **A third way out, where one genuinely exists** (`ST12` item 2, `#1359`).
   *
   * Two answers is the shape this component was built for and stays the default. The exception is a
   * question whose real answer is *neither*: an operator told *save or discard* over an edit they
   * want to keep **without** overwriting the original is being offered two answers to a question
   * with three, and the third — *save it as a copy* — is the one they actually wanted. Todd's path,
   * `ST12`: *open an existing source, edit it, then decide you don't want to overwrite the current
   * one.*
   *
   * Absent on every other confirm, and it must stay that way: three buttons is a menu, and the
   * reason this dialog works is that it asks one question. Add one only where the extra answer
   * **already exists somewhere else on the surface** — this one is the footer's *Save as…*, met by
   * an operator who did not think of it before trying to leave.
   */
  alternative?: { label: string; onSelect: () => void };
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  alternative,
  onConfirm,
  onCancel,
}) => {
  const cancelRef = React.useRef<HTMLButtonElement | null>(null);

  // Focus the *safe* answer, so a reflexive Enter or Space cannot discard anything. The dialog
  // appears in response to a keystroke, which is exactly when a second one is already on its way.
  React.useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const backdrop = useBackdropDismiss(onCancel);

  // **Portaled, so no caller's layout reaches it** (Todd, rehearsing `3d`: *"on the wrong layer…
  // transparent"*). It rendered where it was called, and a list row dims a disabled source with
  // `opacity`, which creates a stacking context and applies to every descendant — so a confirm
  // opened from that row was see-through and sat under the rows below it, whatever its `zIndex`.
  // Every caller inside a row had the defect; the owner pill was the first on a dimmed one.
  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        bg="blackAlpha.600"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={1600}
        p={4}
        // A drag that started inside the dialog and released out here is not a click out here — see
        // `useBackdropDismiss`. This dialog exists to make a discard deliberate, so dismissing it by
        // accident is the failure it was built against, one layer out.
        {...backdrop}
        onKeyDown={(e) => {
          // Never let a key out of here: the surface underneath has its own Escape and Enter handling,
          // and this dialog exists because one of them was reachable when it should not have been.
          e.stopPropagation();
          if (e.key === 'Escape') onCancel();
        }}
      >
        <Box
          role="alertdialog"
          aria-modal="true"
          aria-label={title}
          bg="bg.card"
          borderRadius="8px"
          boxShadow="0 8px 32px rgba(0,0,0,0.18)"
          maxW="420px"
          w="100%"
          p={5}
          onClick={(e) => e.stopPropagation()}
        >
          <VStack align="stretch" gap={3}>
            <Text fontSize="md" fontWeight="600" color="fg.title">
              {title}
            </Text>
            {/* A `div`, not a paragraph: a body may hold a list or a checkbox, which a `<p>` cannot. */}
            {body && (
              <Text as="div" fontSize="sm" color="fg.muted">
                {body}
              </Text>
            )}
            <HStack justify="flex-end" gap={2} pt={1} wrap="wrap">
              {/* Left of Cancel: it is neither the safe answer nor the destructive one, and putting it
                between them would make the destructive button the one a reflexive click lands on. */}
              {alternative && (
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="accent.solid"
                  color="accent.solid"
                  _hover={{ bg: 'accent.subtle' }}
                  onClick={alternative.onSelect}
                >
                  {alternative.label}
                </Button>
              )}
              <Button
                ref={cancelRef}
                size="sm"
                variant="outline"
                borderColor="border.default"
                color="fg.muted"
                _hover={{ bg: 'bg.subtle' }}
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
              {/* **`solidHover`, not `ink`** (Todd, 2026-08-27: *"the 'exit without saving' button
                hides on mouse-over"*). `ink` is a *deeper* shade in light mode and a *lighter*
                one in dark, where it is a foreground colour for tinted surfaces — so hovering
                this button in dark mode took `#D43F3A` to `#e56b66` under white text, dropping
                the label from 4.3:1 to 2.5:1. The pointer arriving made it less readable, which
                on the one dialog standing between an operator and their unsaved work is the
                worst place in the console for it. ~87 other call sites still say `ink`; they are
                converted as they are touched, not swept. */}
              <Button
                size="sm"
                bg={tone === 'danger' ? 'error.solid' : 'accent.solid'}
                color="white"
                _hover={{ bg: tone === 'danger' ? 'error.solidHover' : 'accent.solidHover' }}
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Portal>
  );
};
